import { CommonModule, DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading } from '@angular/router';
import { AuthApiService } from '@api/auth';
import { HOSPITAL_TIMEZONE_OFFSET } from '@core/constants';
import { apiErrorInterceptor, authInterceptor, requestCoalescingInterceptor, requestTimeoutInterceptor } from '@core/interceptors';
import { SESSION_LOGOUT_TRANSPORT, SESSION_MODAL_CLOSER } from '@core/interfaces';
import { IdlePreloadStrategy } from '@lm-shared';
import { NgbConfig, NgbDateParserFormatter, NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';


import { APP_ROUTES } from './app-routing.module';
import { NgbDateCustomParserFormatter } from './core/date-formater';
import { applyNgBootstrapLegacyDefaults } from './core/ng-bootstrap/ng-bootstrap-defaults.utils';
import { TwoLetterWeekdayDatepickerI18n } from './core/ng-bootstrap/two-letter-weekday-datepicker-i18n.service';
import { AppModalCloser } from './core/services/app-modal-closer.service';
import { HelperService } from './helper.service';

/*
 * Root providers of the Lifemaan Partner Platform (was AppModule): interceptor chain, session hooks, translate and
 * toastr wiring. The interceptors are listed in the order AppModule listed them, which is the order they run:
 * auth headers, GET de-duplication, the 30-second ceiling, then error reporting (innermost, so it sees every failure).
 */
export const appConfig: ApplicationConfig = {
    providers: [
    // The app keeps zone-based change detection; Angular 21's bootstrapApplication would otherwise start zoneless.
        provideZoneChangeDetection(),
        importProvidersFrom(
            FormsModule,
            BrowserModule,
            BrowserAnimationsModule,
            ToastrModule.forRoot({
                preventDuplicates: true,
            }),
            NgbModule,
            ReactiveFormsModule,
            CommonModule,
        ),
        provideTranslateService({ loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }) }),
        // Every lazy page is downloaded in the background once the first screen is up, so a click never waits for the next page's code.
        provideRouter(APP_ROUTES, withPreloading(IdlePreloadStrategy)),
        provideHttpClient(
            withInterceptors([authInterceptor, requestCoalescingInterceptor, requestTimeoutInterceptor, apiErrorInterceptor]),
        ),
        // AppModule's constructor did this: ng-bootstrap 7's look (no modal animations) on today's ng-bootstrap.
        provideAppInitializer(() => applyNgBootstrapLegacyDefaults(inject(NgbConfig))),
        HelperService,
        Title,
        { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
        { provide: NgbDatepickerI18n, useClass: TwoLetterWeekdayDatepickerI18n },
        // Every date on screen is the hospital's clock, IST, whatever zone the browser is in (review MR !1 N16).
        { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: HOSPITAL_TIMEZONE_OFFSET } },
        AuthApiService,
        { provide: SESSION_LOGOUT_TRANSPORT, useExisting: AuthApiService },
        { provide: SESSION_MODAL_CLOSER, useExisting: AppModalCloser },
    ],
};
